/**
 * Exports ImageData to a PNG or JPEG Blob.
 *
 * @param imageData - The ImageData to export
 * @param contentType - The MIME type for the output format (default: "image/png")
 * @returns Promise resolving to a Blob of the exported image
 *
 * @example
 * ```ts
 * const imageData = RandomImage()
 * const pngBlob = await ExportImage(imageData)
 * const jpegBlob = await ExportImage(imageData, "image/jpeg")
 * ```
 */
export const ExportImage = async (
  imageData: ImageData,
  contentType: "image/jpeg" | "image/png" = "image/png"
): Promise<Blob> => {
  if (typeof OffscreenCanvas !== "undefined") {
    const canvas = new OffscreenCanvas(imageData.width, imageData.height)
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Failed to get canvas 2d context")
    ctx.putImageData(imageData, 0, 0)
    return canvas.convertToBlob({ type: contentType })
  }

  if (typeof document !== "undefined") {
    const canvas = document.createElement("canvas")
    canvas.width = imageData.width
    canvas.height = imageData.height
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Failed to get canvas 2d context")
    ctx.putImageData(imageData, 0, 0)

    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error("Failed to create blob from canvas"))
        }
      }, contentType)
    })
  }

  throw new Error("No canvas implementation available")
}
